/**
 * B.5 — Mark a NIPE-authored draft so it's visually distinct in the
 * vault and SharePoint.
 *
 * Two markers applied:
 *   1. Filename prefix `nipe_ai_` (collapses to single prefix if the
 *      caller already added one; no double-prefix).
 *   2. YAML frontmatter `source: nipe` (added if frontmatter exists;
 *      injects a new frontmatter block if it doesn't).
 *
 * The UI uses the frontmatter tag to render a "Draft" badge in vault
 * rows (also B.5; see VaultRow.tsx). Both markers travel through
 * portable-site projections (they end up in SP files unchanged).
 *
 * Pure helper — no I/O, no React, no Convex.
 */

const NIPE_PREFIX = 'nipe_ai_';

/** Add the `nipe_ai_` prefix unless already present. */
export function nipeAuthoredFileName(fileName: string): string {
  if (fileName.startsWith(NIPE_PREFIX)) return fileName;
  // Preserve any leading directory segments in case the model included
  // them (we still prefix the basename only).
  const slashIdx = fileName.lastIndexOf('/');
  if (slashIdx === -1) return `${NIPE_PREFIX}${fileName}`;
  const dir = fileName.slice(0, slashIdx + 1);
  const base = fileName.slice(slashIdx + 1);
  if (base.startsWith(NIPE_PREFIX)) return fileName;
  return `${dir}${NIPE_PREFIX}${base}`;
}

/**
 * Add `source: nipe` to YAML frontmatter. If the body has no
 * frontmatter, insert a fresh `---` block at the top. If frontmatter
 * exists but lacks `source:`, append `source: nipe`. If `source:`
 * already has a value, leave it untouched (don't clobber user/model
 * intent).
 */
export function nipeAuthoredBody(body: string): string {
  const fmMatch = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(body);
  if (!fmMatch) {
    return `---\nsource: nipe\n---\n\n${body}`;
  }
  const frontmatterBlock = fmMatch[1] ?? '';
  if (/^\s*source\s*:/m.test(frontmatterBlock)) {
    // Source field already set; preserve as-is.
    return body;
  }
  const newFrontmatter = `${frontmatterBlock.trimEnd()}\nsource: nipe`;
  return body.replace(fmMatch[0], `---\n${newFrontmatter}\n---\n`);
}

export interface NipeAuthoredInput {
  fileName: string;
  body: string;
}

export interface NipeAuthoredResult {
  fileName: string;
  body: string;
}

/** Apply both markers in a single call. */
export function markNipeAuthored(
  input: NipeAuthoredInput,
): NipeAuthoredResult {
  return {
    fileName: nipeAuthoredFileName(input.fileName),
    body: nipeAuthoredBody(input.body),
  };
}
